/**
 * ============================================================
 * KEJJA OS — src/components/InstallAppCard.tsx
 * ============================================================
 */

import { useState } from 'react';
import { Download, Smartphone, Check, Share2 } from 'lucide-react';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function InstallAppCard() {
  const { state, triggerInstall } = useInstallPrompt();
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  if (state === 'installed') {
    return (
      <Card className="border-emerald-300/50 bg-emerald-50/50 dark:bg-emerald-950/10">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
            <Check className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-semibold">App installed</div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Kejja is on your home screen.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (state === 'ios') {
    return (
      <Card className="border-sky-300/50 bg-sky-50/50 dark:bg-sky-950/10">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-sky-500/20 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold">Install Kejja on iOS</div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Tap the Share button <Share2 className="w-3 h-3 inline mx-0.5" /> then "Add to Home Screen"
              </p>
              <Button size="sm" variant="ghost" className="text-xs mt-2 h-7" onClick={() => setShowIOSHelp(!showIOSHelp)}>
                {showIOSHelp ? 'Hide steps' : 'Show steps'}
              </Button>
              {showIOSHelp && (
                <ol className="text-[11px] text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                  <li>Make sure you're in Safari (not Chrome).</li>
                  <li>Tap the Share icon at the bottom of the screen.</li>
                  <li>Scroll down and tap "Add to Home Screen".</li>
                  <li>Tap "Add" in the top right.</li>
                  <li>Open Kejja from your home screen.</li>
                </ol>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (state === 'available') {
    return (
      <Card className="border-sky-300/60 bg-sky-50/60 dark:bg-sky-950/20">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-sky-500/20 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
            <Download className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">Install Kejja</div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Get a home-screen icon, offline-ready, push notifications.
            </p>
            <Button
              size="sm"
              className="bg-sky-600 hover:bg-sky-700 text-white text-xs mt-2"
              onClick={() => triggerInstall()}
            >
              <Download className="w-3 h-3 mr-1" /> Install app
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // unsupported — hide entirely
  return null;
}
